import { z } from "zod";

// CLIMATE ZONES

export const CLIMATE_ZONES = ["H1", "H2", "H3"] as const;
export type ClimateZone = (typeof CLIMATE_ZONES)[number];

export const CLIMATE_ZONES_RECORD: Record<ClimateZone, string[]> = {
  H1: [
    "01",
    "02",
    "03",
    "05",
    "08",
    "10",
    "14",
    "15",
    "19",
    "21",
    "23",
    "25",
    "27",
    "28",
    "38",
    "39",
    "42",
    "43",
    "45",
    "51",
    "52",
    "54",
    "55",
    "57",
    "58",
    "59",
    "60",
    "61",
    "62",
    "63",
    "67",
    "68",
    "69",
    "70",
    "71",
    "73",
    "74",
    "75",
    "76",
    "77",
    "78",
    "80",
    "87",
    "88",
    "89",
    "90",
    "91",
    "92",
    "93",
    "94",
    "95",
  ],
  H2: [
    "04",
    "07",
    "09",
    "12",
    "16",
    "17",
    "18",
    "22",
    "24",
    "26",
    "29",
    "31",
    "32",
    "33",
    "35",
    "36",
    "37",
    "40",
    "41",
    "44",
    "46",
    "47",
    "49",
    "50",
    "53",
    "56",
    "64",
    "65",
    "72",
    "79",
    "81",
    "82",
    "84",
    "85",
    "86",
  ],
  H3: [
    "06",
    "11",
    "13",
    "20",
    "30",
    "34",
    "66",
    "83",
    "971",
    "972",
    "973",
    "974",
    "976",
  ],
};

export function getClimateZone(zipcode: string): ClimateZone {
  // Extract the first two digits of the postal code
  const departmentCode = zipcode.trim().substring(0, 2);
  // Some have 3 digits like 971
  const departmentCodeBis = zipcode.trim().substring(0, 3);

  if (
    CLIMATE_ZONES_RECORD.H1.includes(departmentCode) ||
    CLIMATE_ZONES_RECORD.H1.includes(departmentCodeBis)
  ) {
    return "H1";
  }

  if (
    CLIMATE_ZONES_RECORD.H2.includes(departmentCode) ||
    CLIMATE_ZONES_RECORD.H2.includes(departmentCodeBis)
  ) {
    return "H2";
  }

  if (
    CLIMATE_ZONES_RECORD.H3.includes(departmentCode) ||
    CLIMATE_ZONES_RECORD.H3.includes(departmentCodeBis)
  ) {
    return "H3";
  }

  throw new Error(
    `Impossible de déterminer la zone climatique pour le code postal "${zipcode}"`,
  );
}

// SECTORS

export const SECTOR_DATA = [
  "resi",
  "ter",
  "indu",
  "Résidentiel collectif",
] as const;

export type Sector = (typeof SECTOR_DATA)[number];

export const MAIN_SECTORS = ["resi", "ter"] as const satisfies Sector[];
export type MainSector = (typeof MAIN_SECTORS)[number];

export const LOCATION_SECTORS = {
  resi: "Résidentiel",
  ter: "Tertiaire",
  indu: "Industriel",
  "Résidentiel collectif": "Résidentiel collectif",
} as const satisfies Record<Sector, string>;

export type SectorLabel =
  (typeof LOCATION_SECTORS)[keyof typeof LOCATION_SECTORS];

export const SectorStringSchema = z.union([
  z.string(),
  z.record(z.enum(MAIN_SECTORS), z.string()).optional(),
]);

export const SectorZoneNumberSchema = z.union([
  z.number(),
  z.record(z.enum(MAIN_SECTORS), z.number()),
  z.record(z.enum(MAIN_SECTORS), z.record(z.enum(CLIMATE_ZONES), z.number())),
]);
export type SectorZoneNumber = z.infer<typeof SectorZoneNumberSchema>;

// @todo Typiquement le genre de truc qui devrait avoir ses tests
export const extractSectorZoneNumber = ({
  data,
  mainSector,
  climateZone,
}: {
  data: SectorZoneNumber;
  mainSector: MainSector;
  climateZone: ClimateZone;
}) => {
  if (!data) {
    return null;
  }

  if (typeof data === "number") {
    return data;
  }

  const sectorData = data[mainSector];
  if (!sectorData) {
    return null;
  }

  if (typeof sectorData === "number") {
    return sectorData;
  }

  const sectorZoneData = sectorData[climateZone];
  if (!sectorZoneData) {
    return null;
  }

  return sectorZoneData;
};

// ENERGY TYPES

export const ENERGY_TYPES = [
  "Gaz",
  "Electrique",
  "Fioul",
  "Géothermie",
  "Biomasse",
  "Autres",
  "reseau de chaleur",
] as const;

export type EnergyType = (typeof ENERGY_TYPES)[number];

// HEATING TYPES

export const HEATING_SYSTEMS = [
  "Non connu",
  "Individuel",
  "Collectif chaufferie",
  "Collectif urbain",
  "collectif",
  "Autre",
] as const;

export type HeatingSystem = (typeof HEATING_SYSTEMS)[number];

// LOCATION ADDRESS

export const LOCATION_ADDRESS_DETAILS = z.object({
  streetNumber: z.string().nullish(),
  streetName: z.string().nullish(),
  zipcode: z.string(),
  city: z.string(),
});

export type LocationAddressDetails = z.infer<typeof LOCATION_ADDRESS_DETAILS>;

export const LOCATION_PLACE_DETAILS = z.object({
  name: z.string().nullish(),
  formattedAddress: z.string().nullish(),
  googlePlaceId: z.string().nullish(),
  longitude: z.number().nullish(),
  latitude: z.number().nullish(),
});

export type LocationPlaceDetails = z.infer<typeof LOCATION_PLACE_DETAILS>;

export const EstimatedCostParamsSchema = z.object({
  surfaceArea: z.number(),
  mainSector: z.enum(MAIN_SECTORS),
});

export enum XFactorsKey {
  SURFACE_AREA = "surfaceArea",
  FACADE_AREA = "facadeArea",
  GLAZING_AREA = "glazingArea",
  NB_STOREYS = "nbStoreys",
  NB_UNITS = "nbUnits",
  NB_BUILDINGS = "nbBuildings",
  MAIN_SECTOR = "mainSector",
  CLIMATE_ZONE = "climateZone",
  ELECTRICITY_CONSUMPTION_PER_SQUARE_METER = "electricityConsumptionPerSquareMeter",
}

// X FACTOR PARAMS
export const XFactorParamsSchema = z.object({
  surfaceArea: z.number().nullish(),
  facadeArea: z.number().nullish(),
  glazingArea: z.number().nullish(),
  nbStoreys: z.number().nullish(),
  nbUnits: z.number().nullish(),
  nbBuildings: z.number().nullish(),
  mainSector: z.enum(MAIN_SECTORS).nullish(),
  climateZone: z.enum(CLIMATE_ZONES).nullish(),
  electricityConsumptionPerSquareMeter: z.number().nullish(),
});

export type XFactorParams = z.infer<typeof XFactorParamsSchema>;

export const X_FACTOR_LABELS = {
  surfaceArea: "Emprise au sol",
  facadeArea: "Surface de la façade",
  glazingArea: "Surface vitrée",
  nbStoreys: "Nombre de niveaux",
  nbUnits: "Nombre de lots",
  nbBuildings: "Nombre de sites",
  mainSector: "Secteur",
  climateZone: "Zone climatique",
  electricityConsumptionPerSquareMeter: "Conso/m²",
} as const satisfies Record<XFactorsKey, string>;

export const AT_RISK_DPE_LABELS = ["E", "F", "G"];

export const DPE_BY_KWH = {
  A: 70,
  B: 110,
  C: 180,
  D: 250,
  E: 330,
  F: 420,
  G: 421,
};

export function getDpeLabel(kwh: number | null) {
  if (kwh === null) {
    return "NC";
  }
  if (kwh <= DPE_BY_KWH.A) {
    return "A";
  } else if (kwh <= DPE_BY_KWH.B) {
    return "B";
  } else if (kwh <= DPE_BY_KWH.C) {
    return "C";
  } else if (kwh <= DPE_BY_KWH.D) {
    return "D";
  } else if (kwh <= DPE_BY_KWH.E) {
    return "E";
  } else if (kwh <= DPE_BY_KWH.F) {
    return "F";
  }
  // At or above the G threshold.
  return "G";
}
